/*
  EGODRIFT Predictive Search
  Version: 3.4.4

  Shopify production filename:
  assets/predictive-search.js

  Changes in this version:
  - Uses “Search for anything” while an empty search field has focus.
  - Restores the rotating prompt after blur when no query was entered.
  - Keeps multi-instance result scoping and mobile-menu height handling from V3.3.
  - Calculates the desktop search start from the first visible main-menu item, with a logo-based fallback.
  - Leaves predictive drawer width to the search form itself, avoiding a duplicated toolbar offset.
*/

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.placeholderTerms = [];
    this.placeholderIndex = 0;
    this.placeholderTimer = null;
    this.focusPlaceholder = this.input.dataset.egoSearchFocusPlaceholder || 'Search for anything';
    this.headerSearch = this.closest('.ego-header-search');
    this.headerDetails = this.headerSearch?.querySelector('details') || null;
    this.boundHeaderLayoutUpdate = null;
    this.boundHeaderToggle = null;

    this.setupEventListeners();
    this.setupRotatingPlaceholder();
    this.setupHeaderSearchLayout();
  }

  disconnectedCallback() {
    if (this.placeholderTimer) window.clearInterval(this.placeholderTimer);
    if (this.boundHeaderLayoutUpdate) window.removeEventListener('resize', this.boundHeaderLayoutUpdate);
    if (this.headerDetails && this.boundHeaderToggle) {
      this.headerDetails.removeEventListener('toggle', this.boundHeaderToggle);
    }
  }

  setupHeaderSearchLayout() {
    if (!this.headerSearch) return;

    this.boundHeaderLayoutUpdate = this.updateHeaderSearchLayout.bind(this);
    this.boundHeaderToggle = () => {
      if (!this.headerDetails?.open) return;
      window.requestAnimationFrame(this.boundHeaderLayoutUpdate);
    };

    window.addEventListener('resize', this.boundHeaderLayoutUpdate, { passive: true });
    this.headerDetails?.addEventListener('toggle', this.boundHeaderToggle);
    this.updateHeaderSearchLayout();
  }

  updateHeaderSearchLayout() {
    if (!this.headerSearch) return;

    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.headerSearch.style.removeProperty('--ego-header-search-start');
      return;
    }

    const header = this.headerSearch.closest('.header');
    const logo = header?.querySelector('.header__heading-link') || header?.querySelector('.header__heading');

    if (!header || !logo) return;

    const headerRect = header.getBoundingClientRect();
    const logoRect = logo.getBoundingClientRect();
    const firstVisibleMenuItem = Array.from(
      header.querySelectorAll('.header__inline-menu .list-menu--inline > li')
    ).find((item) => {
      const rect = item.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    const logoInset = Math.max(0, logoRect.left - headerRect.left);
    const logoFallbackStart = logoRect.right - headerRect.left + logoInset;
    const menuStart = firstVisibleMenuItem
      ? firstVisibleMenuItem.getBoundingClientRect().left - headerRect.left
      : logoFallbackStart;
    const desiredStart = Math.max(logoRect.right - headerRect.left, menuStart);
    const minimumSearchWidth = 320;
    const controlsWidth = 54;
    const maximumStart = Math.max(0, headerRect.width - minimumSearchWidth - controlsWidth);
    const searchStart = Math.min(maximumStart, Math.max(0, desiredStart));

    this.headerSearch.style.setProperty('--ego-header-search-start', `${Math.round(searchStart)}px`);

  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('blur', this.onInputBlur.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  setupRotatingPlaceholder() {
    const placeholderData = this.input.dataset.egoSearchPlaceholders;

    if (!placeholderData) return;

    this.placeholderTerms = placeholderData
      .split('|')
      .map((term) => term.trim())
      .filter(Boolean);

    if (!this.placeholderTerms.length) return;

    this.input.placeholder = this.placeholderTerms[0];

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || this.placeholderTerms.length === 1) return;

    this.placeholderTimer = window.setInterval(() => {
      if (this.input.value.length || document.activeElement === this.input) return;

      this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderTerms.length;
      this.input.placeholder = this.placeholderTerms[this.placeholderIndex];
    }, 3000);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[id^="predictive-search-results-groups-wrapper"]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.input.placeholder = this.focusPlaceholder;
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onInputBlur() {
    if (this.getQuery().length || !this.placeholderTerms.length) return;

    this.input.placeholder = this.placeholderTerms[this.placeholderIndex] || this.placeholderTerms[0];
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) return;

    const previousTermOccurrences = currentButtonText.split(previousTerm).length - 1;
    if (previousTermOccurrences > 1) {
      // The previous term matches part of the button text and not just the search term.
      return;
    }

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        document.querySelectorAll('predictive-search').forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  getResultsInstanceSuffix() {
    const inputId = this.input.id || 'ego-search';
    return inputId.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  scopeResultsMarkup(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup.trim();

    const suffix = this.getResultsInstanceSuffix();
    const idMap = new Map();

    template.content.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const scopedId = `${originalId}--${suffix}`;

      idMap.set(originalId, scopedId);
      element.id = scopedId;
    });

    const referenceAttributes = [
      'aria-labelledby',
      'aria-describedby',
      'aria-controls',
      'aria-owns',
      'aria-activedescendant',
      'for',
    ];

    referenceAttributes.forEach((attribute) => {
      template.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const scopedValue = value
          .split(/\s+/)
          .map((reference) => idMap.get(reference) || reference)
          .join(' ');

        element.setAttribute(attribute, scopedValue);
      });
    });

    template.content.querySelectorAll('a[href^="#"]').forEach((link) => {
      const fragmentId = link.getAttribute('href').slice(1);
      const scopedId = idMap.get(fragmentId);
      if (scopedId) link.setAttribute('href', `#${scopedId}`);
    });

    return template.innerHTML;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = this.scopeResultsMarkup(resultsMarkup);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const mobileMenuPanel = this.closest('.ego-mobile-menu__panel');
    const isCompactHeaderSearch =
      window.matchMedia('(max-width: 989px)').matches && this.closest('.ego-header-search');

    if (mobileMenuPanel) {
      const panelBottom = mobileMenuPanel.getBoundingClientRect().bottom;
      const resultsTop = this.predictiveSearchResults.getBoundingClientRect().top;
      this.resultsMaxHeight = Math.max(180, panelBottom - resultsTop - 16);
      return this.resultsMaxHeight;
    }

    if (isCompactHeaderSearch) {
      const resultsTop = this.predictiveSearchResults.getBoundingClientRect().top;
      this.resultsMaxHeight = Math.max(240, viewportHeight - resultsTop);
      return this.resultsMaxHeight;
    }

    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    this.resultsMaxHeight = Math.max(240, viewportHeight - headerBottom);
    return this.resultsMaxHeight;
  }

  open() {
    if (window.matchMedia('(max-width: 989px)').matches && this.closest('.ego-header-search')) {
      this.resultsMaxHeight = false;
    }

    const resultsMaxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.predictiveSearchResults.style.maxHeight = `${resultsMaxHeight}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
